import React from "react";
import { Dialog as BaseDialog } from "Components/Base/Feedback/Dialog";
import { DialogActions } from "Components/Base/Feedback/DialogActions";
import { DialogTitle } from "Components/Base/Feedback/DialogTitle";
import { DialogContent } from "Components/Base/Feedback/DialogContent";
import { Button } from "Components/Base/Inputs/Button";
import { Paper } from "Components/Base/Surfaces/Paper";
import Draggable from "react-draggable";
import useScrollBox from "Components/Hooks/useScrollBox";

/**
 * Wraps the content of the dialog in a draggable Paper component.
 * @param {Object} props The props passed to the Paper component.
 * @returns {React.Component} A draggable version of the Paper component.
 */
function PaperComponent(props) {
  return (
    <Draggable
      handle="#dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper
        {...props}
        aria-label="base-dialog"
        aria-describedby="dialog-description"
      />
    </Draggable>
  );
}

/**
 * @deprecated, use Dialog instead.
 * A dialog component with an optional close button and draggable feature.
 * @param {string} [props.closeText=null] - The text for the close button. If null, no close button is rendered.
 * @param {Function} props.handleClose - The function to call when the close button is clicked.
 * @param {string} props.title - The title of the dialog.
 * @param {string} props.text - The content of the dialog.
 * @param {boolean} props.open - Controls the visibility of the dialog.
 * @param {string} [props.maxWidth="xs"] - The maximum width of the dialog. Defaults to "xs".
 * @param {boolean} [props.draggable=true] - Enables or disables the draggable feature. Defaults to true.
 * @param {boolean} [props.scrollable=true] - Enables or disables the scrollable feature. Defaults to false.
 * @param {boolean} props.fullWidth - Determines if the dialog should be full-width.
 * @returns {React.Component} A dialog component with a close button.
 */
const DialogCloseButton = ({
  closeText = null,
  handleClose,
  title,
  text,
  open,
  maxWidth = "xs",
  draggable = true,
  scrollable = false,
  fullWidth,
}) => {
  const renderContent = () => {
    return (
      <>
        {title && (
          <DialogTitle
            id="dialog-title"
            style={draggable ? { cursor: "move" } : undefined}
          >
            {title}
          </DialogTitle>
        )}
        <DialogContent id="dialog-description">{text}</DialogContent>
        <DialogActions>
          {closeText && <Button onClick={handleClose} label={closeText} />}
        </DialogActions>
      </>
    );
  };

  return (
    <BaseDialog
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      aria-labelledby="base-dialog"
      aria-describedby="dialog-description"
      {...(draggable ? { PaperComponent: PaperComponent } : {})}
      open={open}
    >
      {scrollable ? useScrollBox(renderContent()) : renderContent()}
    </BaseDialog>
  );
};

export default DialogCloseButton;
